<template>
	<div class="page flex flex-col items-end pb-32 lg:pb-0">
	  <SideBar>
		<div class="h-screen relative">
		  <GreenBall
			class="steps__ball w-full"
			start="bottom"
			offset="-2rem"
			cutout="0"
		  />
		  <img
			src="@/assets/img/windmill-sidebar.png"
			alt=""
			class="absolute bottom-28 h-96 2xl:h-2/3 z-0"
		  />
		  <div class="text-left w-3/4 mx-auto absolute z-10 left-24">
			<div class="text-3xl">
			  <div class="text-grey-3 font-normal">
				{{ $t('auto_investment.setup.title_sidebar_1') }}
			  </div>
			  <div class="text-grey-3 font-medium">
				{{ $t('auto_investment.self') }}
			  </div>
			</div>
			<ul class="ml-8 mt-8 text-grey-2">
			  <transition v-for="item in steps" :key="item" name="fade">
				<li class="mb-4">
				  <img
					src="@/assets/img/step_done.png"
					alt=""
					class="w-6 absolute"
				  />
				  <span class="ml-9 text-smfont-bold">
					{{ item }}
				  </span>
				</li>
			  </transition>
			</ul>
		  </div>
		</div>
	  </SideBar>
	  <div class="strategies__desktop-container">
		<div
		  v-if="autoInvestmentToEdit"
		  class="
			flex-grow flex flex-col
			justify-between
			lg:w-2/3
			2xl:w-4/5
			text-left
		  "
		>
		  <Back
			:to="'/auto-investment'"
			:title="$t('auto_investment.edit.title')"
		  />
		  <p class="hidden lg:block text-base text-grey-3 mb-8">
			{{ $t('auto_investment.edit.subtitle') }}
		  </p>
		  <div class="mb-6">
			<p class="text-base font-semibold text-grey-2 mb-12">
			  {{ $t('auto_investment.setup.interest_rate.title') }}
			</p>
			<Slider
			  v-model="interestRate"
			  :min="autoinvest_min_interest"
			  :max="autoinvest_max_interest"
			  :step="1"
			  :tooltips="true"
			  showTooltip="always"
			  :format="formatPercentageTooltip"
			  class="slider-auto-investment"
			  @update="sliderModified()"
			/>
			<div class="flex justify-between mt-6">
			  <p class="font-bold text-grey-2 text-base">{{ autoinvest_min_interest }}%</p>
			  <p class="font-bold text-grey-2 text-base">{{ autoinvest_max_interest}}%</p>
			</div>
		  </div>
		  <div class="mb-6">
			<p class="text-base font-semibold text-grey-2 mb-12">
			  {{ $t('auto_investment.setup.max_term.title') }}
			</p>
			<Slider
			  v-model="maxTerm"
			  :min="1"
			  :max="10"
			  :step="1"
			  :tooltips="true"
			  showTooltip="always"
			  :format="formatMaxTermTooltip"
			  class="slider-auto-investment"
			  @update="sliderModified()"
			/>
			<div class="flex justify-between mt-6">
			  <p class="font-bold text-grey-2 text-base">
				{{ $t('wording.year', 1) }}
			  </p>
			  <p class="font-bold text-grey-2 text-base">
				{{ $t('wording.year', 10) }}
			  </p>
			</div>
		  </div>
		  <div class="mb-6">
			<p class="text-base font-semibold text-grey-2 mb-12">
			  {{ $t('auto_investment.setup.wallet_balance.title') }}
			</p>
			<Slider
			  v-model="walletBalance"
			  :min="10"
			  :max="100"
			  :step="10"
			  :tooltips="true"
			  showTooltip="always"
			  :format="formatPercentageTooltip"
			  class="slider-auto-investment"
			  @update="sliderModified()"
			/>
			<div class="flex justify-between mt-6">
			  <p class="font-bold text-grey-2 text-base">10%</p>
			  <p class="font-bold text-grey-2 text-base">100%</p>
			</div>
		  </div>
		  <div class="mb-6">
			<p class="text-base font-semibold text-grey-2 mb-12">
			  {{ $t('auto_investment.setup.risk_rating.title') }}
			</p>
			<Slider
			  v-model="riskRating"
			  :min="0"
			  :max="99.9"
			  :step="11.1"
			  :tooltips="true"
			  :format="formatRiskTooltip"
			  class="slider-auto-investment"
			  @update="sliderModified()"
			/>
			<div class="flex justify-between mt-6">
			  <p class="font-bold text-grey-2 text-base">A+</p>
			  <p class="font-bold text-grey-2 text-base">D</p>
			</div>
		  </div>
		  <div class="mb-6">
			<Form ref="form" v-slot="{ errors, meta }" class="form">
			  <p class="text-base font-semibold text-grey-2 mb-4 lg:mb-6">
				{{ $t('auto_investment.setup.project_min.title') }}
			  </p>
			  <GeneralInput
				v-model:data="projectMin"
				type="dollar"
				name="projectMin"
				:error="errors.projectMin"
				:locked="checkAmountCheck('projectMin')"
				:label="
				  checkAmountCheck('projectMin') ? '' : $t('invest.amount.title')
				"
				:rules="
				  projectMinCheck
					? ''
					: projectMax
					? 'minValue:500|maxValue:' +
					  dollarToNumberFormatting(projectMax) +
					  ',1'
					: 'minValue:500|maxValue:2499900,1'
				"
			  />
			  <GeneralInput
				v-model:data="projectMinCheck"
				class="custom-checkbox"
				type="checkbox"
				name="projectMinCheck"
			  >
				<template #label>
				  {{
					$t('auto_investment.setup.project_min.info_box', {
					  amount: dollarFormatting(minInvestment)
					})
				  }}
				</template>
			  </GeneralInput>
			  <p class="text-base font-semibold text-grey-2 my-4 lg:my-6">
				{{ $t('auto_investment.setup.project_max.title') }}
			  </p>
			  <GeneralInput
				v-model:data="projectMax"
				type="dollar"
				name="projectMax"
				:error="errors.projectMax"
				:locked="checkAmountCheck('projectMax')"
				:label="
				  checkAmountCheck('projectMax') ? '' : $t('invest.amount.title')
				"
				:rules="
				  projectMaxCheck
					? ''
					: projectMin
					? 'minValue:' +
					  dollarToNumberFormatting(projectMin) +
					  '|maxValue:2499900,1' +
					  ',1'
					: 'minValue:500|maxValue:2499900,1'
				"
			  />
			  <GeneralInput
				v-model:data="projectMaxCheck"
				class="custom-checkbox"
				type="checkbox"
				name="projectMaxCheck"
			  >
				<template #label>
				  {{ $t('auto_investment.setup.project_max.info_box') }}
				</template>
			  </GeneralInput>
			  <div
				class="
				  flex flex-col
				  lg:flex-row lg:items-center
				  mt-10
				  text-center
				  lg:text-left
				"
			  >
				<router-link
				  to="/auto-investment"
				  class="
					text-orange-1
					font-semibold
					text-base
					uppercase
					lg:flex-grow lg:pl-20
					mb-8
					lg:mb-0
				  "
				>
				  {{ capitalize($t('wording.cancel')) }}
				</router-link>
				<GpButton
				  :disabled="!formChanged || !meta.valid || maxTerm === 0"
				  @click.once="updateAutoInvestment()"
				>
				  {{ $t('wording.confirm') }}
				</GpButton>
			  </div>
			</Form>
		  </div>
		</div>
	  </div>
	</div>
  </template>
  
  <script>
  import SideBar from '@/components/general/SideBar.vue'
  import Back from '@/components/general/Back.vue'
  import GreenBall from '@/components/general/GreenBall.vue'
  import GpButton from '@/components/general/GpButton.vue'
  import Slider from '@vueform/slider'
  import useGlobalHelpers from '@/mixins/useGlobalHelpers.js'
  import useAutoInvestment from '@/mixins/useAutoInvestment.js'
  import { computed, onMounted, ref, inject } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Form } from 'vee-validate'
  import GeneralInput from '@/components/general/Input.vue'
  import { useStore } from 'vuex'
  import { useRouter } from 'vue-router'
  import useStrategies from '@/mixins/useStrategies.js'
  
  export default {
	name: 'EditAutoInvestment',
	components: {
	  SideBar,
	  GreenBall,
	  Slider,
	  Back,
	  Form,
	  GeneralInput,
	  GpButton
	},
	props: {
	  autoInvestment: {
		type: String,
		default: () => {}
	  }
	},
	setup(props) {
	  const i18n = useI18n({ useScope: 'global' })
	  const { dollarFormatting, capitalize, dollarToNumberFormatting } =
		useGlobalHelpers()
	  const { buildAutoInvestmentsSteps } =
		useAutoInvestment()
	  const store = useStore()
	  const autoInvestmentToEdit = ref(null)
	  const interestRate = ref(null)
	  const maxTerm = ref(null)
	  const walletBalance = ref(null)
	  const riskRating = ref(null)
	  const projectMin = ref(null)
	  const sliderHasChanged = ref(false)
	  const projectMax = ref(null)
	  const projectMinCheck = ref(false)
	  const projectMaxCheck = ref(false)
  
	  const steps = ref([])
	  const cookies = inject('vue-cookie')
	  const profile = ref(cookies.getCookie('profile'))
  
	  const router = useRouter()
  
	  const { levelRisk, risktoSliderConversion, valueRisk } = useStrategies()
  
	  const checkAmountCheck = (value) => {
		if (
		  (value === 'projectMin' && projectMinCheck.value === true) ||
		  (value === 'projectMax' && projectMaxCheck.value === true)
		) {
		  if (value === 'projectMin') projectMin.value = null
		  if (value === 'projectMax') projectMax.value = null
		  return true
		}
	  }
  
	  const minInvestment = computed(() => {
		return store.state.general?.settings?.min_investment ?? 1000
	  })
  
	  const sliderModified = () => {
		sliderHasChanged.value = true
	  }
  
	  const formatPercentageTooltip = (value) => {
		return `${Math.round(value)}%`
	  }
  
	  const formatMaxTermTooltip = (value) => {
		return i18n.t('wording.year', Math.round(value))
	  }
  
	  const activeAutoInvestment = computed(() => {
		return store.getters.getActiveAutoInvestment
	  })
  
	  const formatRiskTooltip = (value) => {
		return levelRisk(parseFloat(value.toFixed(1)))
	  }
  
	  const amountInputsFilled = computed(() => {
		return (
		  (projectMin.value || projectMinCheck.value) &&
		  (projectMax.value || projectMaxCheck.value)
		)
	  })
  
	  const autoinvest_min_interest = computed(() => {
		return store.state.general.settings?.autoinvest_min_interest ?? 2
	  })
  
	  const autoinvest_max_interest = computed(() => {
		return store.state.general.settings?.autoinvest_max_interest ?? 10
	  })
  
	  const formChanged = computed(() => {
		if (
		  sliderHasChanged.value === true ||
		  (amountInputsFilled.value &&
			autoInvestmentToEdit.value.project_min &&
			dollarToNumberFormatting(projectMin.value) !=
			  autoInvestmentToEdit.value.project_min) ||
		  (autoInvestmentToEdit.value.project_max &&
			dollarToNumberFormatting(projectMax.value) !=
			  autoInvestmentToEdit.value.project_max) ||
		  (!autoInvestmentToEdit.value.project_max && projectMax.value) ||
		  (!autoInvestmentToEdit.value.project_min && projectMin.value)
		) {
		  return true
		} else {
		  return false
		}
	  })
  
	  const updateAutoInvestment = () => {
		formChanged.value = false
		const data = {
		  min_interest: interestRate.value[0],
		  max_interest: interestRate.value[1],
		  max_term: maxTerm.value,
		  balance_percentage: walletBalance.value,
		  max_risk: valueRisk(levelRisk(riskRating.value)),
		  project_min: projectMinCheck.value ? false : dollarToNumberFormatting(projectMin.value),
		  project_max: projectMaxCheck.value ? false : dollarToNumberFormatting(projectMax.value)
		}
		store.dispatch('setLoading', true)
		if (autoInvestmentToEdit.value.id) {
		  data['id'] = autoInvestmentToEdit.value.id
		  store
			.dispatch('updateAutoInvestment', data)
			.then(() => {
			  const popup = {
				title: i18n.t('auto_investment.setup.confirmation.popup.title'),
				align: 'center',
				side: false,
				iconBg: 'bg-green-5',
				icon: '/img/done_step.svg',
				primaryButton: i18n.t(
				  'auto_investment.setup.confirmation.popup.btn'
				),
				primaryLink:"/auto-investment",
				bgLink: "/auto-investment"
			  }
			  store.dispatch('setLoading', false)
			  store.dispatch('setPopup', popup)
			})
			.catch((e) => {
			  store.dispatch('setError', e)
			  store.dispatch('setLoading', false)
			  store.dispatch('forceRedirect', '/auto-investment')
			  formChanged.value = true
			})
		} else {
		  store
			.dispatch('createAutoInvestment', data)
			.then(() => {
			  const popup = {
				title: i18n.t('auto_investment.setup.confirmation.popup.title'),
				align: 'center',
				side: false,
				iconBg: 'bg-green-5',
				icon: '/img/done_step.svg',
				primaryButton: i18n.t(
				  'auto_investment.setup.confirmation.popup.btn'
				),
				primaryLink:"/auto-investment",
				bgLink: "/auto-investment"
			  }
			  store.dispatch('setLoading', false)
			  store.dispatch('setPopup', popup)
			})
			.catch((e) => {
			  store.dispatch('setError', e)
			  store.dispatch('setLoading', false)
			  store.dispatch('forceRedirect', '/auto-investment')
			  formChanged.value = true
			})
		}
	  }
  
	  onMounted(async () => {
		if (!props.autoInvestment) {
		  router.push({
			name: 'auto-investment'
		  })
		} else {
		  autoInvestmentToEdit.value = JSON.parse(props.autoInvestment)
  
		  let buildSteps = buildAutoInvestmentsSteps()
		  steps.value = buildSteps.steps
		  interestRate.value = [
			autoInvestmentToEdit.value.min_interest,
			autoInvestmentToEdit.value.max_interest
		  ]
		  maxTerm.value = parseInt(autoInvestmentToEdit.value.max_term)
		  walletBalance.value = parseInt(
			autoInvestmentToEdit.value.balance_percentage
		  )
		  riskRating.value = risktoSliderConversion(autoInvestmentToEdit.value.max_risk)
		  if (!autoInvestmentToEdit.value.project_min)
			projectMinCheck.value = true
		  else
			projectMin.value = dollarFormatting(
			  parseInt(autoInvestmentToEdit.value.project_min)
			)
		  if (!autoInvestmentToEdit.value.project_max)
			projectMaxCheck.value = true
		  else
			projectMax.value = dollarFormatting(
			  parseInt(autoInvestmentToEdit.value.project_max)
			)
		}
	  })
  
	  return {
		interestRate,
		dollarFormatting,
		formatPercentageTooltip,
		formatMaxTermTooltip,
		formatRiskTooltip,
		autoInvestmentToEdit,
		maxTerm,
		walletBalance,
		riskRating,
		projectMin,
		projectMax,
		checkAmountCheck,
		projectMaxCheck,
		projectMinCheck,
		capitalize,
		activeAutoInvestment,
		formChanged,
		sliderModified,
		amountInputsFilled,
		updateAutoInvestment,
		dollarToNumberFormatting,
		buildAutoInvestmentsSteps,
		steps,
		autoinvest_min_interest,
		autoinvest_max_interest,
		minInvestment
	  }
	}
  }
  </script>
  
  <style>
  .slider-auto-investment {
	--slider-connect-bg: #b3dabb;
	--slider-bg: #4f59631a;
	--slider-handle-ring-color: #ffffff;
	--slider-handle-bg: #b3dabb;
	--slider-handle-ring-width: 6px;
	--slider-handle-radius: 1000px;
	--slider-tooltip-bg: #b3dabb;
	--slider-tooltip-py: 0.25rem;
	--slider-tooltip-px: 0.25rem;
  }
  
  .slider-horizontal .slider-handle {
	right: -16px;
  }
  </style>
  